import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";
import {
  TextField,
  Typography,
  Button,
  Box,
  Grid,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Modal,
  Fade,
  CircularProgress
} from "@mui/material";
import classes from "./VendorInvite.module.css";
import { useSelector } from "react-redux";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import deletePopupImg from "../../../assets/images/png/Group 2@2x.png";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^\d{10}$/;
const VENDOR_NAME_MAX_LEN = 30;
const VENDOR_EMAIL_MAX_LEN = 100;

const getManualInviteErrors = ({ vendorType, vendorName, vendorEmail, vendorMobile }) => {
  const nextErrors = { vendorName: "", vendorEmail: "", vendorMobile: "" };
  const nextName = (vendorName ?? "").trim();
  const nextEmail = (vendorEmail ?? "").trim();
  const nextMobile = (vendorMobile ?? "").trim();

  if (!nextName) {
    nextErrors.vendorName = `${vendorType} name is required.`;
  } else if (nextName.length < 2) {
    nextErrors.vendorName = `${vendorType} name must be at least 2 characters.`;
  } else if (nextName.length > VENDOR_NAME_MAX_LEN) {
    nextErrors.vendorName = `${vendorType} name must be ${VENDOR_NAME_MAX_LEN} characters or less.`;
  }

  if (!nextEmail) {
    nextErrors.vendorEmail = `${vendorType} email is required.`;
  } else if (nextEmail.length > VENDOR_EMAIL_MAX_LEN) {
    nextErrors.vendorEmail = `${vendorType} email must be ${VENDOR_EMAIL_MAX_LEN} characters or less.`;
  } else if (!EMAIL_REGEX.test(nextEmail)) {
    nextErrors.vendorEmail = "Enter a valid email address.";
  }

  if (!nextMobile) {
    nextErrors.vendorMobile = `${vendorType} mobile number is required.`;
  } else if (!MOBILE_REGEX.test(nextMobile)) {
    nextErrors.vendorMobile = "Enter a valid 10-digit mobile number.";
  }

  return nextErrors;
};

const VendorInvite = () => {
  const loginData = useSelector((state) => state.loginData.value);
  const [buyer_token] = useState(loginData?.user?.token);
  const Datas = useSelector((state) => state.loginData.value);

  const inviteTextFieldSx = {
    "& .MuiOutlinedInput-root": {
      fontFamily: "gilroyMedium",
    },
    "& .MuiInputBase-input": {
      fontSize: "16px",
      fontFamily: "gilroyMedium",
    },
    "& .MuiInputLabel-root": {
      fontFamily: "gilroyMedium",
      fontSize:"14px"
    },
    "& .MuiInputLabel-shrink": {
      fontFamily: "gilroyMedium",
      backgroundColor: "#fff",
      padding: "0 4px",
      borderRadius: "2px",
    },
    "& .MuiFormHelperText-root": {
      fontFamily: "gilroyBold",
      fontSize: "13px",
      fontWeight: 700,
    },
  };

  const [vendorName, setVendorName] = useState("");
  const [vendorEmail, setVendorEmail] = useState("");
  const [vendorMobile, setVendorMobile] = useState("");
  const [securityamount, setSecurityAmount] = useState("");
  const [file, setFile] = useState(null);
  const [inviteMode, setInviteMode] = useState("manual"); // manual | bulk

  const [inviteLoading, setInviteLoading] = useState(false);

  const [invitePopupOpen, setInvitePopupOpen] = useState(false);
  const [invitePopupContent, setInvitePopupContent] = useState({
    image: successPopupImg,
    heroText: "",
    description: "",
  });

  const [vendorType, setVendorType] = useState("Vendor");
  const [dragging, setDragging] = useState(false);
  const [vendorTotalCount, setVendorTotalCount] = useState(0);
  const [vendorCountLoading, setVendorCountLoading] = useState(false);

  // ==== VENDOR LIMIT GUARD STATES (Same as VendorsTab) ====
  const [limitPopupOpen, setLimitPopupOpen] = useState(false);
  const [popupHeroText, setPopupHeroText] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [popupImage, setPopupImage] = useState(deletePopupImg);

  const [manualTouched, setManualTouched] = useState({
    vendorName: false,
    vendorEmail: false,
    vendorMobile: false,
  });
  const [manualErrors, setManualErrors] = useState({
    vendorName: "",
    vendorEmail: "",
    vendorMobile: "",
  });

  const manualCurrentErrors = getManualInviteErrors({ vendorType, vendorName, vendorEmail, vendorMobile });
  const isManualFormValid = Object.values(manualCurrentErrors).every((errorText) => !errorText);

  // vendor limit
  const vendorPlanLimit = Number(Datas?.vendor_count ?? 0) || 0;

  const getTotalVendors = useCallback(async () => {
    try {
      if (!buyer_token || !loginData?.auth_token) return 0;
      const res = await axios.post(
        API_ENDPOINT.get_all_vendor,
        { org_token: buyer_token, page_number: 1 },
        {
          headers: {
            Authorization: `Bearer ${loginData?.auth_token}`,
          },
        }
      );

      console.log("Vendor API raw response:", res.data);

      const totalFromApi =
        Number(res.data?.total_count) ||
        Number(res.data?.total_records) ||
        0;

      const totalVendors =
        totalFromApi ||
        (Array.isArray(res.data?.users) ? res.data.users.length : 0);

      return totalVendors;
    } catch (error) {
      console.error("Error fetching vendor count:", error);
      return 0;
    }
  }, [buyer_token, loginData?.auth_token]);

  const refreshVendorCount = useCallback(async () => {
    setVendorCountLoading(true);
    const totalVendors = await getTotalVendors();
    setVendorTotalCount(totalVendors);
    setVendorCountLoading(false);
  }, [getTotalVendors]);

  useEffect(() => {
    refreshVendorCount();
  }, [refreshVendorCount]);

  useEffect(() => {
    if (inviteMode !== "manual") return;
    if (!manualTouched.vendorName && !manualTouched.vendorEmail && !manualTouched.vendorMobile) return;
    setManualErrors(getManualInviteErrors({ vendorType, vendorName, vendorEmail, vendorMobile }));
  }, [
    inviteMode,
    vendorType,
    manualTouched.vendorName,
    manualTouched.vendorEmail,
    manualTouched.vendorMobile,
    vendorName,
    vendorEmail,
    vendorMobile,
  ]);

  // Guard check: stop invite if limit reached
  const guardLimitAndShow = async () => {
    if (vendorPlanLimit === 0) return false; // no limit set

    const totalVendors = await getTotalVendors();


    if (vendorPlanLimit > 0 && totalVendors >= vendorPlanLimit) {
      setPopupHeroText("Supplier Limit Reached");
      setPopupMessage(
        `Your account can have up to ${vendorPlanLimit} vendors. Currently used: ${totalVendors}/${vendorPlanLimit}.`
      );
      setPopupImage(deletePopupImg);
      setLimitPopupOpen(true);
      return true;
    }
    return false;
  };

  //  Handle invite
  const handleInvite = async (e) => {
    e.preventDefault();

    if (inviteMode === "manual") {
      const nextTouched = { vendorName: true, vendorEmail: true, vendorMobile: true };
      setManualTouched(nextTouched);
      const nextErrors = getManualInviteErrors({ vendorType, vendorName, vendorEmail, vendorMobile });
      setManualErrors(nextErrors);
      const hasErrors = Object.values(nextErrors).some(Boolean);
      if (hasErrors) return;
    }

    setInviteLoading(true);

    const atLimit = await guardLimitAndShow();
    if (atLimit) {
      console.log("Invite stopped due to vendor limit reached.");
      setInviteLoading(false);
      return;
    }

    const formData = new FormData();

    if (file) {
      formData.append("vendor_file", file);
      formData.append("buyer_token", buyer_token);
    } else if (vendorEmail && vendorName) {
      formData.append("vendor_mail", vendorEmail);
      formData.append("vendor_name", vendorName);
      formData.append("vendor_mobile", vendorMobile);
      formData.append("securityamount", String(securityamount || "").replace(/,/g, ""));
      formData.append("vendorType", vendorType);
      formData.append("buyer_token", buyer_token);
    } else {
      alert("Please fill vendor details or upload a file.");
      setInviteLoading(false);
      return;
    }

    try {
      const res = await axios.post(API_ENDPOINT.invite_vendor, formData, {
        headers: {
          Authorization: `Bearer ${loginData?.auth_token}`,
        },
      });

      console.log("✅ Invite Response:", res.data);

      const successList = Array.isArray(res.data?.success) ? res.data.success : [];
      const failedList = Array.isArray(res.data?.failed) ? res.data.failed : [];
      const messageText = res.data?.message || "";

      if (successList.length > 0 || failedList.length > 0 || messageText) {
        const totalInvited = successList.length;
        const totalFailed = failedList.length;
        const isFailure = totalFailed > 0 && totalInvited === 0;

        const descriptionParts = [];
        if (messageText) descriptionParts.push(`<div>${String(messageText)}</div>`);
        if (totalInvited || totalFailed) {
          descriptionParts.push(
            `<div style="margin-top:8px;"><strong>Invited:</strong> ${totalInvited} &nbsp; <strong>Failed:</strong> ${totalFailed}</div>`
          );
        }
        if (totalFailed > 0) {
          const failedItems = failedList
            .map((v) => `<li style="margin:2px 0;">${String(v)}</li>`)
            .join("");
          descriptionParts.push(
            `<div style="margin-top:8px;"><strong>Failed:</strong><ul style="margin:6px 0 0 18px; padding:0;">${failedItems}</ul></div>`
          );
        }

        setInvitePopupContent({
          image: isFailure ? deletePopupImg : successPopupImg,
          heroText: isFailure
            ? `${vendorType} invite failed`
            : `${vendorType} invite processed`,
          description: descriptionParts.join(""),
        });
        setInvitePopupOpen(true);

        setVendorName("");
        setVendorEmail("");
        setVendorMobile("");
        setSecurityAmount("");
        setFile(null);
        setManualTouched({ vendorName: false, vendorEmail: false, vendorMobile: false });
        setManualErrors({ vendorName: "", vendorEmail: "", vendorMobile: "" });
        refreshVendorCount();
      } else {
        alert("Invite failed.");
      }
    } catch (err) {
      console.error("Invite Error:", err);
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.response ||
        err?.response?.data?.error ||
        err?.message;

      setInvitePopupContent({
        image: deletePopupImg,
        heroText: `${vendorType} invite failed`,
        description: `<div>${serverMsg ? String(serverMsg) : "Error sending invite."}</div>`,
      });
      setInvitePopupOpen(true);
    } finally {
      setInviteLoading(false);
    }
  };


  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      setFile(file);
    }
  };

  const handleAmountChange = (e) => {
    const digits = String(e.target.value || "").replace(/[^0-9]/g, "");
    if (!digits) {
      setSecurityAmount("");
      return;
    }
    const amountNumber = Number(digits);
    if (Number.isNaN(amountNumber)) return;
    setSecurityAmount(amountNumber.toLocaleString("en-IN"));
  };

  const handleInviteModeChange = (event) => {
    const nextMode = event.target.value;
    setInviteMode(nextMode);

    if (nextMode === "manual") {
      setFile(null);
      setManualTouched({ vendorName: false, vendorEmail: false, vendorMobile: false });
      setManualErrors({ vendorName: "", vendorEmail: "", vendorMobile: "" });
      return;
    }

    setVendorName("");
    setVendorEmail("");
    setVendorMobile("");
    setSecurityAmount("");
    setManualTouched({ vendorName: false, vendorEmail: false, vendorMobile: false });
    setManualErrors({ vendorName: "", vendorEmail: "", vendorMobile: "" });
  };

  // Close limit popup - same as VendorsTab
  const handleCloseLimitPopup = () => {
    setLimitPopupOpen(false);
  };

  return (
    <div style={ { marginTop: "1rem" } }>
      <p
        style={ {
          fontSize: "2.4rem",
          fontWeight: "600",
          color: "#3a3a3c",
          fontFamily: "gilroyBold",
          marginLeft: "0px"
        } }
      >{ `${vendorType} Invite` }</p>

      <Box display="flex" alignItems="center" gap={ 1 } mb={ 1 }>
        <Typography sx={ { fontSize: "15px", color: "#555", fontFamily: "gilroyMedium" } }>
          Supplier Quota:
        </Typography>
        <Typography sx={ { fontSize: "15px", fontWeight: 700, color: "#111", fontFamily: "gilroyBold" } }>
          { vendorCountLoading
            ? "Loading..."
            : vendorPlanLimit > 0
              ? `${vendorTotalCount}/${vendorPlanLimit}`
              : `${vendorTotalCount}` }
        </Typography>
        { vendorPlanLimit > 0 && !vendorCountLoading && (
          <Typography
            sx={ {
              fontSize: "13px",
              color: vendorTotalCount >= vendorPlanLimit ? "#b91c1c" : "#2e7d32",
              fontWeight: 600,
              fontFamily: "gilroyMedium",
            } }
          >
            ({ Math.max(vendorPlanLimit - vendorTotalCount, 0) } remaining)
          </Typography>
        ) }
      </Box>

      <Box display="flex" alignItems="center" gap={ 2 }>
        <FormLabel
          component="legend"
          sx={ {
            fontSize: "14px",
            fontWeight: "bold",
            whiteSpace: "nowrap",
            fontFamily: "gilroyBold",
          } }
        >
          Select the Type
        </FormLabel>

        <RadioGroup
          row
          value={ vendorType }
          onChange={ (e) => setVendorType(e.target.value) }
          sx={ { fontSize: "14px" } }
        >
          <FormControlLabel
            value="Supplier"
            control={ <Radio /> }
            label={ <Typography sx={ { fontSize: "14px", fontFamily: "gilroyMedium" } }>Supplier</Typography> }
          />
          <FormControlLabel
            value="Merchant"
            control={ <Radio /> }
            label={ <Typography sx={ { fontSize: "14px", fontFamily: "gilroyMedium" } }>Merchant</Typography> }
          />
        </RadioGroup>
      </Box>

      { vendorType && (
        <>
          <Box display="flex" alignItems="center" gap={ 2 } mb={ 2 } mt={ 2 }>
            <FormLabel
              component="legend"
              sx={ {
                fontSize: "14px",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                fontFamily: "gilroyBold",
              } }
            >
              Invite Mode
            </FormLabel>

            <RadioGroup
              row
              value={ inviteMode }
              onChange={ handleInviteModeChange }
              sx={ { fontSize: "14px" } }
            >
              <FormControlLabel
                value="manual"
                control={ <Radio /> }
                label={
                  <Typography sx={ { fontSize: "14px", fontFamily: "gilroyMedium" } }>
                    Invite Supplier manually
                  </Typography>
                }
              />
              <FormControlLabel
                value="bulk"
                control={ <Radio /> }
                label={
                  <Typography sx={ { fontSize: "14px", fontFamily: "gilroyMedium" } }>
                    Bulk Supplier invite
                  </Typography>
                }
              />
            </RadioGroup>
          </Box>

          { inviteMode === "manual" && (
            <Grid container spacing={ 2 } mb={ 2 }>
              <Grid item xs={ 3 }>
                <TextField
                  label={ `${vendorType} Name` }
                  fullWidth
                  value={ vendorName }
                  onChange={ (e) => {
                    const nextValue = e.target.value;
                    setVendorName(nextValue);
                    if (!manualTouched.vendorName) {
                      setManualTouched((prev) => ({ ...prev, vendorName: true }));
                    }
                    setManualErrors(
                      getManualInviteErrors({
                        vendorType,
                        vendorName: nextValue,
                        vendorEmail,
                        vendorMobile,
                      })
                    );
                  } }
                  onBlur={ () => {
                    if (!manualTouched.vendorName) {
                      setManualTouched((prev) => ({ ...prev, vendorName: true }));
                    }
                    setManualErrors(getManualInviteErrors({ vendorType, vendorName, vendorEmail, vendorMobile }));
                  } }
                  error={ Boolean(manualTouched.vendorName && manualErrors.vendorName) }
                  helperText={ manualTouched.vendorName ? manualErrors.vendorName : "" }
                  inputProps={ { maxLength: VENDOR_NAME_MAX_LEN } }
                  variant="outlined"
                  sx={ inviteTextFieldSx }
                />
              </Grid>
              <Grid item xs={ 3 }>
                <TextField
                  label={ `${vendorType} Email` }
                  fullWidth
                  type="email"
                  value={ vendorEmail }
                  onChange={ (e) => {
                    const nextValue = e.target.value;
                    setVendorEmail(nextValue);
                    if (!manualTouched.vendorEmail) {
                      setManualTouched((prev) => ({ ...prev, vendorEmail: true }));
                    }
                    setManualErrors(
                      getManualInviteErrors({
                        vendorType,
                        vendorName,
                        vendorEmail: nextValue,
                        vendorMobile,
                      })
                    );
                  } }
                  onBlur={ () => {
                    if (!manualTouched.vendorEmail) {
                      setManualTouched((prev) => ({ ...prev, vendorEmail: true }));
                    }
                    setManualErrors(getManualInviteErrors({ vendorType, vendorName, vendorEmail, vendorMobile }));
                  } }
                  error={ Boolean(manualTouched.vendorEmail && manualErrors.vendorEmail) }
                  helperText={ manualTouched.vendorEmail ? manualErrors.vendorEmail : "" }
                  inputProps={ { maxLength: VENDOR_EMAIL_MAX_LEN } }
                  variant="outlined"
                  sx={ inviteTextFieldSx }
                />
              </Grid>
              <Grid item xs={ 3 }>
                <TextField
                  label={ `${vendorType} Mobile` }
                  fullWidth
                  value={ vendorMobile }
                  type="text"
                  onChange={ (e) => {
                    const value = e.target.value;
                    if (/^\d{0,10}$/.test(value)) {
                      setVendorMobile(value);
                      if (!manualTouched.vendorMobile) {
                        setManualTouched((prev) => ({ ...prev, vendorMobile: true }));
                      }
                      setManualErrors(
                        getManualInviteErrors({
                          vendorType,
                          vendorName,
                          vendorEmail,
                          vendorMobile: value,
                        })
                      );
                    }
                  } }
                  onBlur={ () => {
                    if (!manualTouched.vendorMobile) {
                      setManualTouched((prev) => ({ ...prev, vendorMobile: true }));
                    }
                    setManualErrors(getManualInviteErrors({ vendorType, vendorName, vendorEmail, vendorMobile }));
                  } }
                  error={ Boolean(manualTouched.vendorMobile && manualErrors.vendorMobile) }
                  helperText={ manualTouched.vendorMobile ? manualErrors.vendorMobile : "" }
                  inputProps={ { maxLength: 10 } }
                  variant="outlined"
                  sx={ inviteTextFieldSx }
                />
              </Grid>

              <Grid item xs={ 3 }>
                <TextField
                  label={ `${vendorType} Security Amount` }
                  fullWidth
                  value={ securityamount }
                  type="text"
                  onChange={ handleAmountChange }
                  variant="outlined"
                  sx={ inviteTextFieldSx }
                />
              </Grid>
            </Grid>
          ) }

          { inviteMode === "bulk" && (
            <div>
              <Typography
                sx={ {
                  fontSize: "16px",
                  fontFamily: "gilroyBold",
                  color: "#3a3a3c",
                  mb: 1,
                } }
              >
                Bulk Supplier Invite
              </Typography>
              {/* File Upload Section */ }
              <Box mb={ 2 }>
                <div className={ classes.docField }>
                  <div
                    className={ `${classes.fileUploadWrapper} ${dragging ? classes.drag_over : ""
                      }` }
                    onDragOver={ handleDragOver }
                    onDragLeave={ handleDragLeave }
                    onDrop={ handleDrop }
                  >
                    <div className={ classes.file_upload }>
                      { !file ? (
                        <>
                          <label className={ classes.DocText }>
                            <span>Drag and drop file here</span>
                            <input
                              type="file"
                              accept=".csv,.xlsx"
                              style={ { display: "none" } }
                              onChange={ handleFileChange }
                              id="fileInput"
                            />
                            <p>(Supported format CSV/Excel)</p>
                          </label>
                          <button
                            onClick={ () =>
                              document.getElementById("fileInput").click()
                            }
                            style={ {
                              backgroundColor: "#1976d2",
                              color: "white",
                              padding: "8px 16px",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                            } }
                            onMouseOver={ (e) =>
                              (e.target.style.backgroundColor = "#115293")
                            }
                            onMouseOut={ (e) =>
                              (e.target.style.backgroundColor = "#1976d2")
                            }
                          >
                            Browse
                          </button>
                        </>
                      ) : (
                        <div
                          style={ {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "100px",
                          } }
                        >
                          <span>
                            { file.name } ({ (file.size / 1024).toFixed(2) } KB)
                          </span>
                          <button
                            onClick={ () => setFile(null) }
                            style={ {
                              background: "transparent",
                              border: "none",
                              color: "red",
                              cursor: "pointer",
                              fontWeight: "600",
                            } }
                          >
                            Remove
                          </button>
                        </div>
                      ) }
                    </div>
                  </div>
                </div>
              </Box>

              <Button
                variant="outlined"
                style={ { marginBottom: "12px", fontSize: "12px", fontFamily: "gilroyMedium" } }
                onClick={ () => {
                  const csvContent = `${vendorType} Name,${vendorType} Email,${vendorType} Mobile,Security Amount\nExample ${vendorType.toLowerCase()},${vendorType.toLowerCase()}@example.com,9999999999,10000`;
                  const blob = new Blob([csvContent], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `sample_${vendorType}_invite.csv`;
                  a.click();
                } }
              >
                Download Sample CSV
              </Button>
            </div>
          ) }

          {/* Send Invite Button */ }
      <Button
        type="submit"
        onClick={ (e) => handleInvite(e) }
        variant="contained"
        color="primary"
            style={ { height: "4rem", fontSize: "12px", fontFamily: "gilroyBold" } }
            disabled={
              inviteLoading ||
              (inviteMode === "manual" ? !isManualFormValid : !file)
            }
          >
            {inviteLoading ? (
              <>
                <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
                Sending...
              </>
            ) : (
              "Send Invite"
            )}
          </Button>
        </>
      ) }

      {/* Invite Result Popup (same pattern as RFQ/PO) */ }
      <Modal open={ invitePopupOpen }>
        <Fade in={ invitePopupOpen }>
          <Box>
            <SuccessPopupDashboard
              popupImage={ invitePopupContent.image }
              popupHeroText={ invitePopupContent.heroText }
              popupDescription={ invitePopupContent.description }
              popBtn={ true }
              btnText={ "Okay" }
              buyerBtn={ true }
              buyerBtnOutline={ true }
              btnClick={ () => setInvitePopupOpen(false) }
            />
          </Box>
        </Fade>
      </Modal>

      {/* Vendor Limit Reached Popup - EXACT SAME AS VendorVendorsTab */ }
      <Modal open={ limitPopupOpen }>
        <Fade in={ limitPopupOpen }>
          <Box>
            <SuccessPopupDashboard
              popupImage={ popupImage }
              popupHeroText={ popupHeroText }
              popupDescription={ popupMessage }
              popBtn={ true }
              btnText={ "Okay" }
              buyerBtn={ true }
              buyerBtnOutline={ true }
              btnClick={ handleCloseLimitPopup }
            />
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};

export default VendorInvite;

